/* eslint-disable no-unused-vars */
import { Context, Data, Effect, Layer } from "effect";
import { EventBus } from "./EventBus";

/**
 * Event publisher errors.
 * @since 0.1.3
 */
export class EventPublishError extends Data.TaggedError("@/EventPublisher/EventPublishError")<{
  readonly cause?: unknown;
  readonly message: string;
  readonly eventType?: string;
}> {}

/**
 * Event publisher interface for publishing domain events.
 * 
 * Provides a simplified interface for publishing domain events without
 * needing to manage event bus subscriptions or lifecycle directly.
 * 
 * @interface EventPublisherSchema
 * @since 0.1.3
 * @see {@link EventPublisherLive} for default implementation
 * @example
 * ```typescript
 * import { EventPublisher } from '@invoiceddd/application';
 * 
 * const eventPublisher = yield* EventPublisher;
 * await eventPublisher.publish(invoiceCreatedEvent);
 * ```
 */
export interface EventPublisherSchema {
  /**
   * Publish a domain event.
   * @template T - The event type with a `type` property
   * @param event - The domain event to publish
   * @returns Effect that never fails (fire-and-forget semantics)
   * @since 0.1.3
   * @example
   * await eventPublisher.publish({ 
   *   type: 'InvoiceCreated', 
   *   invoiceId: '123' 
   * })
   */
  readonly publish: <T extends { type: string }>(event: T) => Effect.Effect<void, never>;
}

/**
 * Context tag for EventPublisher service.
 * Used for dependency injection in Effect-TS.
 * @since 0.1.3
 * @see {@link EventPublisherSchema}
 */
export const EventPublisher = Context.GenericTag<EventPublisherSchema>("@/EventPublisherTag");

/**
 * Default EventPublisher implementation that uses the EventBus to publish events.
 * 
 * This implementation ensures that all subscribers receive the events properly
 * and provides fire-and-forget semantics (never fails, logs errors instead).
 * 
 * @since 0.1.3
 * @see {@link EventPublisherSchema}
 * @see {@link EventBus}
 * @example
 * ```typescript
 * import { EventPublisherLive, EventBusLive } from '@invoiceddd/application';
 * 
 * const AppLayer = Layer.mergeAll(
 *   EventBusLive,
 *   EventPublisherLive,
 *   // ... other layers
 * );
 * ```
 */
export const EventPublisherLive = Layer.effect(
  EventPublisher,
  Effect.gen(function* () {
    const eventBus = yield* EventBus;

    const implementation: EventPublisherSchema = {
      publish: <T extends { type: string }>(event: T) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: we might need an integration for that!
        return eventBus.publish(event as any).pipe(
          Effect.tap(() => Effect.log(`[EventPublisher] Published event: ${event.type}`)),
          Effect.tapError(error => {
            // Log the error but don't fail - event publishing should be fire-and-forget
            return Effect.logError(
              `[EventPublisher] Failed to publish event ${event.type}:`,
              error
            );
          }),
          Effect.catchAll(() => {
            // Log the error but don't fail - event publishing should be fire-and-forget
            return Effect.void;
          })
        );
      },
    };

    return EventPublisher.of(implementation);
  })
);
